/**
 * Cookie and tracking consent – compliant across regions (GDPR, etc.).
 * - Store: tracking (true = analytics/marketing, false = essential only), askedAt (ms).
 * - Show banner: never answered, or declined and 7+ days since last ask (re-ask once in 7 days).
 * - Pushes consent to Google Tag Manager / gtag (Consent Mode v2) when user accepts or declines.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { pushConsentUpdate } from '../utils/consentMode';

const STORAGE_KEY = 'gradedplay_consent';
const REASK_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && typeof data.askedAt === 'number') {
      return { tracking: !!data.tracking, askedAt: data.askedAt };
    }
  } catch (e) {
    // ignore
  }
  return null;
}

function saveConsent(tracking, askedAt = Date.now()) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tracking, askedAt }));
  } catch (e) {
    // ignore
  }
}

/** True if user has given consent for analytics/tracking. */
function hasTrackingConsent() {
  const stored = loadConsent();
  return stored && stored.tracking === true;
}

/** True if we should show the consent banner (never answered, or declined and 7+ days ago). */
function shouldShowBanner() {
  const stored = loadConsent();
  if (!stored) return true;
  if (stored.tracking) return false;
  return Date.now() - stored.askedAt >= REASK_DAYS_MS;
}

const ConsentContext = createContext();

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return ctx;
}

export function ConsentProvider({ children }) {
  const [consent, setConsentState] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const stored = loadConsent();
    setConsentState(stored);
    setShowBanner(shouldShowBanner());
    if (stored?.tracking === true) {
      pushConsentUpdate(true);
    }
  }, []);

  const setConsent = useCallback((tracking) => {
    const askedAt = Date.now();
    saveConsent(tracking, askedAt);
    setConsentState({ tracking, askedAt });
    setShowBanner(false);
    pushConsentUpdate(tracking);
  }, []);

  const value = {
    consent,
    trackingConsent: consent?.tracking === true,
    showBanner,
    setConsent,
    setShowBanner,
    hasTrackingConsent: consent?.tracking === true,
    shouldShowBanner,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

export { hasTrackingConsent, loadConsent };
